// Frontend Real-User-Monitoring (RUM). Item 106 Phase F of
// docs/PRODUCT-ROADMAP.md — Frontend-RUM with W3C trace-context
// auto-injection and OTLP/HTTP export.
//
// What this module does
// ---------------------
//
//   1. Generates a per-page trace-id at install-time (one trace per
//      page-load; subsequent in-page fetches become child spans of it
//      so an operator can pivot Browser-Click → React-fetch →
//      chi-Handler → pgx-Query → S3-PutObject as a single trace).
//   2. Wraps `window.fetch` and stamps `traceparent` (and an optional
//      `tracestate`) on every outbound request, conforming to W3C
//      Trace Context (https://www.w3.org/TR/trace-context/). Backend
//      `otelhttp` middleware (Item 22) picks the headers up and
//      stitches the cross-tier trace together.
//   3. Records a span per outbound fetch with status_code +
//      duration, batches them, and POSTs the batch to the configured
//      OTLP-HTTP/JSON endpoint (default-OFF — empty endpoint =
//      no-op, air-gapped-friendly).
//   4. Exposes a `markPerf(name)` hook the Loading-State-Library
//      (Item 97 — Skeleton / Spinner / LoadingBar) calls when a busy
//      surface mounts/unmounts, so we get spans for "user clicked
//      button → spinner visible for 1.2 s → response landed".
//
// Why this is hand-rolled instead of pulling in the full
// `@opentelemetry/sdk-trace-web` package family
// ---------------------------------------------------------------
//
// The four OTel-JS packages we'd otherwise depend on
// (`@opentelemetry/api`, `sdk-trace-web`,
// `instrumentation-fetch`, `exporter-trace-otlp-http`) bundle to
// ~120 KiB gzipped after tree-shaking — material for a portal whose
// entire app shell budgets ~250 KiB gz today. We don't need
// resource-detection, B3-multi propagator fallbacks, the synchronous
// span-processor path, or `zone.js`-style async-context for what
// boils down to "stamp two headers, emit OTLP/HTTP/JSON". W3C trace
// context is an interop standard, OTLP/HTTP/JSON is an interop
// standard — collectors don't care which client emitted them. If we
// ever need richer instrumentation (XHR auto-wrap, sub-resource
// timing, the contrib instrumentation matrix) we can swap this for
// the SDK without touching call-sites.
//
// Buffer / export policy
// ----------------------
//
//   - Queue capped at 64 spans; the 65th drops the oldest.
//   - Flush on a 5 s ticker, on `pagehide`, and when the queue hits
//     32 (half-full → push early to keep the tail latency low).
//   - The exporter is best-effort: a 4xx / 5xx / network failure
//     drops the batch silently. We DO NOT call console.error from
//     the fail path because the client-logger (Item 71 Phase E)
//     hooks console.* and we'd loop.
//
// Debug mode: append `?debug=otel-rum` to any URL to mirror every
// outgoing batch to console.debug.

export type SpanKind = 'client' | 'internal';

export interface SpanRecord {
  /** 16-byte trace-id, lowercase hex (32 chars). */
  trace_id: string;
  /** 8-byte span-id, lowercase hex (16 chars). */
  span_id: string;
  /** 8-byte parent span-id, lowercase hex (16 chars), or empty for root. */
  parent_span_id: string;
  /** Span display name. */
  name: string;
  /** UNIX-nanos when the span opened. */
  start_time_unix_nano: string;
  /** UNIX-nanos when the span closed. */
  end_time_unix_nano: string;
  kind: SpanKind;
  attributes: Record<string, string | number | boolean>;
  /** OTLP status: 0 (Unset), 1 (Ok), 2 (Error). */
  status_code: 0 | 1 | 2;
}

const MAX_QUEUE = 64;
const HALF_QUEUE = 32;
const FLUSH_INTERVAL_MS = 5_000;

let installed = false;
let endpointURL = '';
let serviceName = 'orbitalreg-frontend';
let pageTraceID = '';
let rootSpanID = '';
let queue: SpanRecord[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let debugTrace = false;
let originalFetch: typeof fetch | null = null;

// ----- Helpers ------------------------------------------------------

function randomHex(byteLen: number): string {
  // crypto.getRandomValues lives on `window.crypto` in modern browsers
  // and on the Web-Crypto-like `globalThis.crypto` in jsdom test
  // environments. Fall back to Math.random when neither exists so
  // we don't throw during install on an exotic embedded browser.
  const buf = new Uint8Array(byteLen);
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(buf);
  } else {
    for (let i = 0; i < byteLen; i++) buf[i] = Math.floor(Math.random() * 256);
  }
  let out = '';
  for (let i = 0; i < byteLen; i++) {
    out += buf[i].toString(16).padStart(2, '0');
  }
  return out;
}

export function newTraceID(): string {
  return randomHex(16); // 32 hex chars
}

export function newSpanID(): string {
  return randomHex(8); // 16 hex chars
}

const TRACEPARENT_RE = /^00-[0-9a-f]{32}-[0-9a-f]{16}-[0-9a-f]{2}$/;

/** Format a W3C `traceparent` header value. */
export function formatTraceparent(traceID: string, spanID: string, sampled: boolean): string {
  return `00-${traceID}-${spanID}-${sampled ? '01' : '00'}`;
}

/** Round-trip parse for the rare debug path. Exported for tests. */
export function isValidTraceparent(value: string | undefined | null): boolean {
  if (!value) return false;
  return TRACEPARENT_RE.test(value);
}

function nowUnixNanos(): string {
  // performance.timeOrigin + performance.now() gives us sub-ms
  // precision; downconvert to bigint-string for OTLP wire format.
  const ms = performance.timeOrigin + performance.now();
  // (Number → BigInt) for the multiplication so we don't lose
  // precision past 2^53.
  return (BigInt(Math.round(ms)) * 1_000_000n).toString();
}

// ----- Span recording + export -------------------------------------

function enqueueSpan(span: SpanRecord) {
  if (!endpointURL) return;
  queue.push(span);
  if (queue.length > MAX_QUEUE) queue.shift();
  if (queue.length >= HALF_QUEUE) void flushNow('half-full');
}

async function flushNow(reason: 'tick' | 'half-full' | 'pagehide'): Promise<void> {
  if (!endpointURL || queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  const body = JSON.stringify({
    resourceSpans: [
      {
        resource: {
          attributes: [
            { key: 'service.name', value: { stringValue: serviceName } },
          ],
        },
        scopeSpans: [
          {
            scope: { name: 'orbitalreg-frontend-rum', version: '1' },
            spans: batch.map(toOTLPSpan),
          },
        ],
      },
    ],
  });
  if (debugTrace) {
    // eslint-disable-next-line no-console
    console.debug('[otel-rum] flush', reason, batch.length, 'spans');
  }
  try {
    // `keepalive: true` lets the browser finish the request after the
    // page navigates away — same trick clientLogger uses on pagehide.
    await fetch(endpointURL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: reason === 'pagehide',
    });
  } catch {
    // Drop silently — see file-header comment.
  }
}

function toOTLPSpan(s: SpanRecord) {
  const attrs = Object.entries(s.attributes).map(([k, v]) => {
    if (typeof v === 'number') return { key: k, value: { intValue: v } };
    if (typeof v === 'boolean') return { key: k, value: { boolValue: v } };
    return { key: k, value: { stringValue: String(v) } };
  });
  return {
    traceId: s.trace_id,
    spanId: s.span_id,
    parentSpanId: s.parent_span_id || undefined,
    name: s.name,
    kind: s.kind === 'client' ? 3 : 1, // SPAN_KIND_CLIENT / SPAN_KIND_INTERNAL
    startTimeUnixNano: s.start_time_unix_nano,
    endTimeUnixNano: s.end_time_unix_nano,
    attributes: attrs,
    status: { code: s.status_code },
  };
}

// ----- Fetch instrumentation ---------------------------------------

function instrumentFetch() {
  if (typeof globalThis === 'undefined' || originalFetch) return;
  originalFetch = globalThis.fetch.bind(globalThis);
  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const startNanos = nowUnixNanos();
    const startMs = performance.now();
    const spanID = newSpanID();

    // Same-origin only — we don't leak our trace-id to third-party
    // endpoints. The check uses the document base URL when input is a
    // relative URL string.
    const url = typeof input === 'string' || input instanceof URL
      ? new URL(String(input), globalThis.location.href)
      : new URL(input.url, globalThis.location.href);
    const sameOrigin = url.origin === globalThis.location.origin;

    const headers = new Headers(init?.headers || (typeof input === 'string' || input instanceof URL ? undefined : input.headers));
    if (sameOrigin && pageTraceID) {
      // Don't clobber if caller already wrote a traceparent (e.g. a
      // signed-Image proxy that propagates an upstream trace).
      if (!isValidTraceparent(headers.get('traceparent'))) {
        headers.set('traceparent', formatTraceparent(pageTraceID, spanID, true));
      }
    }

    let response: Response | null = null;
    let errored = false;
    try {
      response = await originalFetch!(input, { ...init, headers });
      return response;
    } catch (e) {
      errored = true;
      throw e;
    } finally {
      const dur = performance.now() - startMs;
      enqueueSpan({
        trace_id: pageTraceID,
        span_id: spanID,
        parent_span_id: rootSpanID,
        name: `fetch ${url.pathname}`,
        start_time_unix_nano: startNanos,
        end_time_unix_nano: nowUnixNanos(),
        kind: 'client',
        attributes: {
          'http.method': (init?.method || 'GET').toUpperCase(),
          'http.url': url.toString(),
          'http.status_code': response?.status ?? 0,
          'http.duration_ms': Math.round(dur),
          'orbitalreg.same_origin': sameOrigin,
        },
        status_code: errored || (response && response.status >= 500) ? 2 : 1,
      });
    }
  };
}

// ----- Performance-mark integration (Item 97 surfaces) -------------

/**
 * Record a synchronous performance mark and (when telemetry is
 * enabled) emit a tiny zero-duration internal span. Loading-State
 * surfaces (LoadingBar / Spinner / Skeleton) call this in
 * `useEffect` so an operator can see "spinner became visible at
 * t=320 ms" alongside the fetch span.
 *
 * `name` should be a stable, low-cardinality string like
 * `"loadingbar.show"` — not a user-supplied route path.
 */
export function markPerf(name: string, attrs: Record<string, string | number | boolean> = {}): void {
  if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
    try { performance.mark(`orb:${name}`); } catch { /* mark name conflict — ignore */ }
  }
  if (!endpointURL || !pageTraceID) return;
  const t = nowUnixNanos();
  enqueueSpan({
    trace_id: pageTraceID,
    span_id: newSpanID(),
    parent_span_id: rootSpanID,
    name,
    start_time_unix_nano: t,
    end_time_unix_nano: t,
    kind: 'internal',
    attributes: { 'orbitalreg.mark': true, ...attrs },
    status_code: 1,
  });
}

// ----- Public bootstrap --------------------------------------------

export interface InstallTelemetryOptions {
  /** OTLP/HTTP endpoint. Empty / undefined = no-op. */
  endpointURL?: string;
  /** Override service.name resource attribute. */
  serviceName?: string;
}

/**
 * Initialise frontend RUM. Idempotent — calling twice is a no-op
 * after the first install. Default-OFF when no endpoint is
 * configured (the air-gapped posture).
 *
 * In Vite, the typical invocation is:
 *
 *   installTelemetry({
 *     endpointURL: import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT || '',
 *   });
 */
export function installTelemetry(options: InstallTelemetryOptions = {}): void {
  if (installed) return;
  installed = true;

  endpointURL = (options.endpointURL || '').trim();
  if (options.serviceName) serviceName = options.serviceName;

  if (typeof globalThis !== 'undefined') {
    debugTrace = globalThis.location.search.includes('debug=otel-rum');
  }

  // Always allocate a page trace-id, even when the exporter is off,
  // so downstream code (markPerf, fetch wrapper) doesn't have to
  // null-guard. The traceparent header is still only injected when
  // endpointURL is set OR the operator explicitly enables propagation
  // — we keep it simple: stamp headers iff we'd export, so a fully-
  // air-gapped install transmits nothing.
  pageTraceID = newTraceID();
  rootSpanID = newSpanID();

  if (!endpointURL) return;

  // From here on out: live install.
  instrumentFetch();

  if (typeof globalThis !== 'undefined') {
    flushTimer = setInterval(() => { void flushNow('tick'); }, FLUSH_INTERVAL_MS);
    globalThis.addEventListener('pagehide', () => { void flushNow('pagehide'); });
  }

  // Open a root span for the page so the fetch / mark children have
  // a real parent in the backend's trace tree even if they fire
  // before any user interaction.
  const t0 = nowUnixNanos();
  enqueueSpan({
    trace_id: pageTraceID,
    span_id: rootSpanID,
    parent_span_id: '',
    name: 'page.load',
    start_time_unix_nano: t0,
    end_time_unix_nano: t0,
    kind: 'internal',
    attributes: {
      'page.url': typeof globalThis === 'undefined' ? '' : globalThis.location.pathname,
      'orbitalreg.rum.version': 1,
    },
    status_code: 1,
  });
}

// ----- Test seam ---------------------------------------------------

/**
 * Reset module state. Used by unit tests; not part of the public
 * runtime contract.
 *
 * @internal
 */
export function __resetForTests(): void {
  installed = false;
  endpointURL = '';
  pageTraceID = '';
  rootSpanID = '';
  queue = [];
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = null;
  if (originalFetch && typeof globalThis !== 'undefined') {
    globalThis.fetch = originalFetch;
  }
  originalFetch = null;
  debugTrace = false;
}

/**
 * Read the live page trace-id (32 hex). Empty before installTelemetry
 * has been called. Exposed for components that want to render a
 * "trace id: …" footer for support-ticket attachments.
 */
export function currentTraceID(): string {
  return pageTraceID;
}
